import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CompetitionShareModal from "@/components/CompetitionShareModal";
import { getSeason, getSeasonLeaderboard, getSeasonEvents, shareCardUrl } from "@/lib/api";
import { trackCompetition } from "@/lib/competitionAnalytics";
import { fmtUSD, entityLabel, entityPath } from "@/lib/format";

function statusLabel(status) {
  return String(status || "draft").replace(/_/g, " ");
}

function filterSummary(filters = {}) {
  const parts = [];
  if (filters.chains?.length) parts.push(`chain: ${filters.chains.join(", ")}`);
  if (filters.asset_classes?.length) parts.push(`class: ${filters.asset_classes.join(", ")}`);
  if (filters.asset_subtypes?.length) parts.push(`subtype: ${filters.asset_subtypes.join(", ")}`);
  if (filters.sector_tags?.length) parts.push(`tag: ${filters.sector_tags.join(", ")}`);
  return parts.length ? parts.join(" · ") : "Global eligibility";
}

export default function SeasonPage() {
  const { slug } = useParams();
  const [season, setSeason] = useState(null);
  const [board, setBoard] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        trackCompetition("COMPETITION_EVENT_VIEW", { context: "season", slug });
        const [s, lb, ev] = await Promise.all([
          getSeason(slug),
          getSeasonLeaderboard(slug, { category: "tokens", limit: 50 }),
          getSeasonEvents(slug, { limit: 20 }),
        ]);
        if (!alive) return;
        setSeason(s);
        setBoard(lb);
        setEvents(ev.events || []);
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.detail || "Season not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  return (
    <div className="min-h-screen grain">
      <Navbar category="tokens" />
      <main className="mx-auto max-w-5xl px-4 py-8">
        <Link to="/" className="text-xs text-zinc-500 hover:text-white">← Leaderboard</Link>
        {loading && <div className="mt-8 text-sm text-zinc-500">Loading season…</div>}
        {error && <div className="mt-8 text-sm text-negative" data-testid="season-error">{error}</div>}
        {season && (
          <section className="mt-6" data-testid="season-header">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">BackOnFlip demo season</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight">{season.name}</h1>
            <p className="mt-3 max-w-2xl text-sm text-zinc-400">{season.description}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-zinc-500">
              <span className="rounded-full border border-white/10 px-3 py-1 uppercase tracking-wider">
                {statusLabel(season.status)}
              </span>
              <span className="rounded-full border border-white/10 px-3 py-1">
                {new Date(season.starts_at).toUTCString()} → {new Date(season.ends_at).toUTCString()}
              </span>
              <span className="rounded-full border border-white/10 px-3 py-1">{filterSummary(season.filters)}</span>
            </div>
            <p className="mt-4 text-xs text-zinc-600">
              Ranked by Verified Backing inside the season window only. No rewards. No partnership claim.
            </p>
          </section>
        )}
        {board && (
          <section className="mt-8" data-testid="season-leaderboard">
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Season leaderboard · {board.total} eligible
            </div>
            <div className="space-y-2">
              {(board.entities || []).map((entity) => {
                const rank = entity.season_rank || entity.leaderboard_rank;
                const gap = entity.gap_to_overtake ?? entity.gap_to_next;
                return (
                  <Link
                    key={entity.id}
                    to={entityPath(entity)}
                    className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 transition-colors hover:border-white/20"
                  >
                    <div className="w-10 font-mono text-lg font-black text-zinc-400">#{rank}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display text-base font-bold">{entityLabel(entity)}</div>
                      <div className="text-[11px] text-zinc-500">
                        {entity.chain} · {entity.season_backers || 0} backers · {entity.season_contributions || 0} backs
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-gold">{fmtUSD(entity.season_backing_usd || entity.window_backing_usd)}</div>
                      <div className="text-[10px] text-zinc-500">
                        {rank === 1
                          ? (entity.lead_over_next != null ? `Leading by ${fmtUSD(entity.lead_over_next)}` : "Leader")
                          : (gap != null ? `${fmtUSD(gap)} to overtake` : "—")}
                      </div>
                    </div>
                  </Link>
                );
              })}
              {!board.entities?.length && (
                <div className="rounded-xl border border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                  No Verified Backing in this season yet.
                </div>
              )}
            </div>
          </section>
        )}
        {!!events.length && (
          <section className="mt-10" data-testid="season-events">
            <h2 className="mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Recent activity</h2>
            <div className="space-y-2">
              {events.map((ev) => (
                <div key={ev.id} className="flex items-start justify-between gap-3 rounded-xl border border-white/10 px-4 py-3 text-sm text-zinc-300">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600">{ev.event_type}</div>
                    <div className="mt-1">{ev.share?.headline || ev.event_type}</div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-xs uppercase tracking-wider text-zinc-500 hover:text-gold"
                    data-testid={`season-event-share-${ev.id}`}
                    onClick={() => setShare({
                      headline: ev.share?.headline || ev.event_type,
                      shareText: ev.share?.headline || ev.event_type,
                      canonicalPath: `/season/${slug}`,
                      shareImageUrl: shareCardUrl(ev.id),
                    })}
                  >
                    Share
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      <CompetitionShareModal
        open={!!share}
        onClose={() => setShare(null)}
        headline={share?.headline}
        shareText={share?.shareText}
        canonicalPath={share?.canonicalPath}
        shareImageUrl={share?.shareImageUrl}
      />
    </div>
  );
}
